import InboxCommon, { Conversation } from '@/gf/components/Inbox'
import StoreOrderM from '@/gf/modules/StoreOrder'
import { ConversationRowContent, NewConversation, Pagination, User } from '../../types'
import {
  FetchConversationsDocument,
  FetchMessagesForConversationDocument,
  useCreateConversationMutation,
  useCreateMessageMutation,
  useFetchMessagesForConversationQuery,
  useReadReceiptsForConversationQuery,
} from '../_gen/gql'
import useGqlClient from '../hooks/useGqlClient'
import ConversationM from '../modules/Conversation'
import { StoreOrder } from '../types'
import InboxDetails from './InboxDetails'

const StoreOrderInbox = ({
  conversations,
  refetchConversations,
  page,
  setPage,
  pagination,
  user,
  storeOrder,
  selectedConversationId,
  onSelectedConversationIdChange,
}: {
  conversations: Conversation[]
  refetchConversations: () => Promise<unknown>
  page: number
  setPage: (page: number) => void
  pagination: Pagination
  user: Pick<User, 'id' | 'role'>
  storeOrder: Omit<StoreOrder, 'payOnInvoice'>
  selectedConversationId: string | undefined
  onSelectedConversationIdChange: (conversationId: string | undefined) => void
}) => {
  const gqlClient = useGqlClient()
  const [createMessage] = useCreateMessageMutation({
    client: gqlClient,
    refetchQueries: [FetchMessagesForConversationDocument, FetchConversationsDocument],
  })
  const [createConversation] = useCreateConversationMutation({
    client: gqlClient,
    refetchQueries: [FetchConversationsDocument],
  })

  const displayCustomerContactInfo = StoreOrderM.canDisplayCustomerContactInfo(storeOrder, user)

  const newConversationsUnfiltered = [
    // Non-Admin conversations
    storeOrder.order.requestForQuote
      ? ConversationM.newNonAdminConversationRfq(
          storeOrder.order.requestForQuote,
          storeOrder.store,
          displayCustomerContactInfo
        )
      : ConversationM.newNonAdminConversationStoreOrder(storeOrder, displayCustomerContactInfo),
    // Admin conversations
    storeOrder.order.requestForQuote
      ? ConversationM.newAdminConversationRfq(storeOrder.order.requestForQuote, storeOrder.store)
      : ConversationM.newAdminConversationStoreOrder(storeOrder),
  ]
  // Filter out existing conversations
  const newConversations = ConversationM.filterNewConversations(
    newConversationsUnfiltered,
    conversations
  )

  const conversationRowContent = (
    conversation: Conversation | NewConversation
  ): ConversationRowContent => ({
    name: {
      type: conversation.admin ? 'admin' : 'buyer',
      name: conversation.admin || displayCustomerContactInfo ? conversation.name : 'Customer',
    },
  })

  const selectedConversation =
    conversations &&
    [...conversations, ...newConversations].find((c) => c.id === selectedConversationId)

  const { error: messagesError, ...messagesQueryResult } = useFetchMessagesForConversationQuery({
    client: gqlClient,
    variables: {
      conversationId: selectedConversation?.id as string,
      vendorId: null,
      vendorUserId: null,
    },
    pollInterval: ConversationM.MESSAGES_POLL_INTERVAL,
    // Only refetch if the conversation is unread, so we can fetch the correct "read" status
    onCompleted: selectedConversation?.unreadMessages ? refetchConversations : undefined,
    skip:
      !selectedConversationId || !selectedConversation || 'newConversation' in selectedConversation,
  })

  const { fetchMessagesForConversation: messages } = messagesQueryResult.data || {}

  const { readReceiptsForConversation } =
    useReadReceiptsForConversationQuery({
      client: gqlClient,
      variables: {
        conversationId: selectedConversation?.id as string,
        vendorId: null,
        vendorUserId: null,
      },
      pollInterval: ConversationM.MESSAGES_POLL_INTERVAL,
      skip:
        !selectedConversationId ||
        !selectedConversation ||
        'newConversation' in selectedConversation,
    }).data || {}

  return (
    <InboxCommon
      className="mt-4"
      user={user}
      conversations={conversations}
      selectedConversation={selectedConversation}
      selectedConversationId={selectedConversationId}
      messages={messages}
      messagesError={messagesError}
      readReceiptsForConversation={readReceiptsForConversation}
      onSelectedConversationIdChange={onSelectedConversationIdChange}
      page={page}
      setPage={setPage}
      pagination={pagination}
      newConversations={newConversations}
      displayCustomerContactInfo={displayCustomerContactInfo}
      DetailsForConversation={InboxDetails.StoreOrder(storeOrder)}
      conversationRowContent={conversationRowContent}
      createMessage={(variables) =>
        createMessage({ variables: { ...variables, vendorId: null, vendorUserId: null } })
      }
      createConversation={(variables) =>
        createConversation({
          variables: { ...variables, vendorId: null, vendorUserId: null },
        }).then((resp) => resp.data?.createConversation)
      }
    />
  )
}

export default StoreOrderInbox
